import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import {
  type SessionFileContribution,
  type SessionRecord,
} from "@/shared/domain/sessions";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseAddFilesToSessionVariables {
  sessionId: string;
  files: SessionFileContribution[];
  /**
   * Org that owns the session — always required. Sent as the resource-owner
   * header on the add-files call.
   */
  resourceOwnerId: string;
}

/**
 * Add files to an existing session. On success the session's detail-scoped
 * caches (which include its files list) are invalidated, along with the
 * session lists, since the added files may have shifted the session's
 * aggregate bounds.
 */
export function useAddFilesToSession() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<SessionRecord, Error, UseAddFilesToSessionVariables>({
    mutationFn: ({ sessionId, files, resourceOwnerId }) =>
      sessionService.addFilesToSession(sessionId, files, { resourceOwnerId }),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.detail(record.session_id),
      });
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.lists(),
      });
    },
  });
}
